import { useMemo, useState } from 'react';
import { List, Spin, Input } from 'design-system';
import { SearchOutlined } from '@ant-design/icons';

import DraggableSubjectItem, { Subject } from './DraggableSubjectItem';

interface SubjectListProps {
  subjects: Subject[];
  loading?: boolean;
  onAddToCanvas?: (items: Subject[]) => void;
}
export default function SubjectList({ subjects, loading = false, onAddToCanvas }: SubjectListProps) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  
  // 为每个科目添加索引属性，方便调试
  const subjectsWithIndex = useMemo(() => subjects.map((subject, index) => ({
      ...subject,
      _originalIndex: index // 添加原始索引
    })), [subjects]);
  
  const getFullSubjectName = (subject: Subject) => {
    // 使用code字段作为科目编码
    const code = subject.code || '';
    
    // 如果存在编码，则在名称后添加
    let nameWithCode = subject.name;
    if (code) {
      nameWithCode = `${subject.name} (${code})`;
    }
    
    let fullName = nameWithCode;
    if (subject.parentName) {
      fullName = `${subject.parentName}/${fullName}`;
      if (subject.grandParentName) {
        fullName = `${subject.grandParentName}/${fullName}`;
      }
    }
    return fullName;
  };
  
  // 过滤科目时保留原始索引
  const filteredSubjects = useMemo(() => {
    const filtered = subjectsWithIndex.filter(subject => {
      const fullName = getFullSubjectName(subject);
      const matches = fullName.toLowerCase().includes(searchValue.toLowerCase());
      return matches;
    });
    
    return filtered;
  }, [subjectsWithIndex, searchValue, getFullSubjectName]);
  
  const handleSubjectSelect = (subjectId: string) => {
    setSelectedSubjectId(subjectId === selectedSubjectId ? null : subjectId);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent, subjectId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSubjectSelect(subjectId);
    }
  };
  
  const handleDoubleClick = (subject: Subject) => {
    if (onAddToCanvas) {
      
      // 创建完整的科目对象以确保所有必要属性都存在
      const completeSubject = {
        ...subject,
        // 确保考核维度数据被传递
        assessmentDimension: subject.assessmentDimension,
        // 添加拖拽所需的属性
        _dragName: subject.name,
        _dragFullName: getFullSubjectName(subject)
      };
      
      
      onAddToCanvas([completeSubject]);
    }
  };
  
  if (loading) {
    return (
      <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin tip="加载科目中..." size="large">
          <div style={{ padding: '50px', background: 'rgba(0, 0, 0, 0.05)', borderRadius: '4px' }} />
        </Spin>
      </div>
    );
  }
  
  return (
    <div style={{ height: '100%', padding: '16px', overflow: 'auto' }}>
      <Input
        placeholder="搜索考核科目"
        prefix={<SearchOutlined />}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      
      {searchValue && (
        <div style={{ marginBottom: 8 }}>
          {filteredSubjects.length > 0 ? (
            <div style={{ color: '#1890ff' }}>
              找到 {filteredSubjects.length} 个匹配的科目
            </div>
          ) : (
            <div style={{ color: '#ff4d4f' }}>
              没有找到匹配的科目
            </div>
          )}
        </div>
      )}
      
      <List
        size="small"
        dataSource={filteredSubjects}
        renderItem={(item, index) => (
          <div 
            role="button"
            tabIndex={0}
            onClick={() => handleSubjectSelect(item.id)}
            onKeyDown={(e) => handleKeyDown(e, item.id)}
            onDoubleClick={() => handleDoubleClick(item)}
            style={{ outline: 'none' }}
            data-index={index}
            data-original-index={item._originalIndex}
          >
            <DraggableSubjectItem 
              subject={item} 
              isSelected={item.id === selectedSubjectId}
            />
          </div>
        )}
        style={{ 
          background: '#fff',
          borderRadius: '4px',
        }}
      />
      
      {/* 显示搜索结果的数量 */}
      {filteredSubjects.length > 0 && (
        <div style={{ marginTop: 16, textAlign: 'center', color: '#999' }}>
          共显示 {filteredSubjects.length} 个科目
        </div>
      )}
    </div>
  );
} 